import { useEffect, useState, type FormEvent } from "react";
import { LockKeyhole, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { InlineAlert } from "@/components/admin/feedback";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ApiError, apiErrorMessage } from "@/lib/api";
import type { EntitlementType, Product } from "@/lib/types";

const entitlementTypes: EntitlementType[] = [
  "boolean",
  "number",
  "string",
  "list",
  "version",
];

type FeatureRow = {
  id: number;
  key: string;
  type: EntitlementType;
  existing: boolean;
};

function rowsFromProduct(product: Product): FeatureRow[] {
  return Object.entries(product.feature_catalog ?? {}).map(([key, type], id) => ({
    id,
    key,
    type: type as EntitlementType,
    existing: true,
  }));
}

export function ProductFeatureCatalogDialog({
  product,
  open,
  busy,
  error,
  onOpenChange,
  onSubmit,
}: {
  product: Product | undefined;
  open: boolean;
  busy: boolean;
  error: unknown;
  onOpenChange: (open: boolean) => void;
  onSubmit: (
    featureCatalog: Record<string, EntitlementType>,
    reason: string,
  ) => void;
}) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<FeatureRow[]>([]);
  const [reason, setReason] = useState("");
  const [validation, setValidation] = useState<string>();

  useEffect(() => {
    if (open && product) {
      setRows(rowsFromProduct(product));
      setReason("");
      setValidation(undefined);
    }
  }, [open, product]);

  if (!product) return null;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const keys = rows.map((row) => (row.existing ? row.key : row.key.trim()));
    if (keys.some((key) => !key)) {
      setValidation(
        t("products.features.keyValidation", "Every feature needs a key."),
      );
      return;
    }
    if (new Set(keys).size !== keys.length) {
      setValidation(
        t("products.features.duplicateValidation", "Feature keys must be unique."),
      );
      return;
    }
    if (!reason.trim()) {
      setValidation(
        t(
          "products.features.reasonValidation",
          "A reason is required to save the Feature Catalog.",
        ),
      );
      return;
    }
    setValidation(undefined);
    onSubmit(
      Object.fromEntries(
        rows.map((row) => [row.existing ? row.key : row.key.trim(), row.type]),
      ),
      reason.trim(),
    );
  }

  function addFeature() {
    setRows((current) => [
      ...current,
      {
        id: current.reduce((maximum, row) => Math.max(maximum, row.id), -1) + 1,
        key: "",
        type: "boolean",
        existing: false,
      },
    ]);
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <DialogContent className="flex max-h-[calc(100dvh-2rem)] flex-col overflow-hidden p-0 sm:max-w-2xl sm:p-0">
        <form className="flex min-h-0 flex-1 flex-col" onSubmit={submit}>
          <DialogHeader className="shrink-0 px-5 pt-5 pr-12 sm:px-6 sm:pt-6 sm:pr-12">
            <DialogTitle className="text-base font-semibold text-ink">
              {t("products.features.dialogTitle", "Add Feature Definitions")}
            </DialogTitle>
            <DialogDescription className="mt-2 text-xs leading-5 text-muted">
              {t(
                "products.features.dialogDescription",
                "Add typed Entitlements for new Licenses. Existing feature keys and types are locked in Lean v1.",
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="console-scrollbar mt-4 min-h-0 flex-1 overflow-y-auto px-5 pb-4 sm:px-6">
            <div className="space-y-3">
              {rows.length ? (
                rows.map((row, index) => (
                  <div
                    className="grid min-w-0 gap-2 border-b border-line pb-3 sm:grid-cols-[minmax(0,1fr)_10rem_auto] sm:items-end"
                    key={row.id}
                  >
                    <div className="min-w-0 space-y-2">
                      <Label htmlFor={`feature-key-${row.id}`} className="text-xs">
                        {t("products.features.featureKey", "Feature key")}
                      </Label>
                      <Input
                        id={`feature-key-${row.id}`}
                        className="border-line bg-canvas font-mono"
                        value={row.key}
                        placeholder={t(
                          "products.features.featureKeyPlaceholder",
                          "export.pdf",
                        )}
                        onChange={(event) =>
                          setRows((current) =>
                            current.map((item) =>
                              item.id === row.id
                                ? { ...item, key: event.target.value }
                                : item,
                            ),
                          )
                        }
                        disabled={busy || row.existing}
                        autoFocus={index === 0}
                        aria-invalid={Boolean(validation && !row.key.trim())}
                        aria-describedby={
                          validation ? "feature-catalog-validation" : undefined
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`feature-type-${row.id}`} className="text-xs">
                        {t("products.features.type", "Type")}
                      </Label>
                      <Select
                        id={`feature-type-${row.id}`}
                        className="border-line bg-canvas font-mono"
                        value={row.type}
                        options={entitlementTypes.map((type) => ({
                          label: type,
                          value: type,
                        }))}
                        onValueChange={(value) =>
                          setRows((current) =>
                            current.map((item) =>
                              item.id === row.id
                                ? { ...item, type: value as EntitlementType }
                                : item,
                            ),
                          )
                        }
                        disabled={busy || row.existing}
                      />
                    </div>
                    {row.existing ? (
                      <span
                        className="flex size-9 items-center justify-center text-muted"
                        role="img"
                        aria-label={t(
                          "products.features.locked",
                          "Existing feature is locked",
                        )}
                      >
                        <LockKeyhole className="size-4" aria-hidden="true" />
                      </span>
                    ) : (
                      <Button
                        type="button"
                        variant="danger"
                        size="icon"
                        aria-label={t("products.features.remove", "Remove {{key}}", {
                          key:
                            row.key ||
                            t("products.features.featureKey", "Feature key"),
                        })}
                        onClick={() =>
                          setRows((current) =>
                            current.filter((item) => item.id !== row.id),
                          )
                        }
                        disabled={busy}
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                ))
              ) : (
                <p className="rounded-md border border-line bg-canvas px-3 py-4 text-xs leading-5 text-muted">
                  {t(
                    "products.features.empty",
                    "No features yet. Add the first feature for License Entitlements.",
                  )}
                </p>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={addFeature}
                disabled={busy}
              >
                <Plus className="size-4" aria-hidden="true" />
                {t("products.features.add", "Add feature")}
              </Button>
            </div>
            <div className="mt-5 space-y-2 border-t border-line pt-4">
              <Label htmlFor="feature-catalog-reason" className="text-xs">
                {t("products.features.reason", "Reason")} {" "}
                <span className="text-danger">*</span>
              </Label>
              <Textarea
                id="feature-catalog-reason"
                rows={3}
                value={reason}
                placeholder={t(
                  "products.features.reasonPlaceholder",
                  "Add License Entitlements",
                )}
                onChange={(event) => setReason(event.target.value)}
                disabled={busy}
                required
                aria-invalid={Boolean(validation && !reason.trim())}
                aria-describedby={
                  validation ? "feature-catalog-validation" : undefined
                }
              />
            </div>
            {validation ? (
              <p
                id="feature-catalog-validation"
                className="mt-3 text-xs text-danger"
                role="alert"
              >
                {validation}
              </p>
            ) : null}
            {error ? (
              <div className="mt-4">
                <InlineAlert
                  title={t(
                    "products.features.saveError",
                    "Feature Catalog was not saved",
                  )}
                  message={
                    error instanceof ApiError && error.status === 409
                      ? t(
                          "products.features.conflictError",
                          "The Feature Catalog changed while you were editing. The latest catalog is loaded; review it and retry.",
                        )
                      : apiErrorMessage(error)
                  }
                />
              </div>
            ) : null}
          </div>
          <DialogFooter className="mt-0 shrink-0 border-t border-line px-5 py-4 sm:px-6">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={busy}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button type="submit" variant="primary" disabled={busy}>
              {busy
                ? t("common.saving", "Saving…")
                : t("products.features.save", "Save Feature Catalog")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
